import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Calendar, Ruler, Users2, Link2Off } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';

/** What the user picked to link a purchase order to. */
export type OrderLinkSelection =
  | { type: 'booking'; id: string; label: string }
  | { type: 'field_quote'; id: string; label: string }
  | { type: 'lead'; id: number; label: string }
  | { type: 'none' };

interface LinkOrderTargetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (selection: OrderLinkSelection) => void;
  /** Show a "Remove link" action (used when editing an existing PO's links). */
  allowUnlink?: boolean;
}

type LinkView = 'options' | 'booking' | 'field_quote' | 'lead';

interface RowItem {
  key: string;
  label: string;
  sub: string;
  extra?: string;
  selection: OrderLinkSelection;
}

const customerName = (c: { first_name: string | null; last_name: string | null } | null): string =>
  `${c?.first_name ?? ''} ${c?.last_name ?? ''}`.trim();

const fmtDate = (d: string | null | undefined): string => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtPrice = (amount: number | null | undefined): string => {
  if (amount == null || Number.isNaN(Number(amount))) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(amount));
};

const bookingAddress = (b: { booking_address?: string | null }): string =>
  (b.booking_address || '').trim();

const VIEW_TITLES: Record<LinkView, string> = {
  options: 'Link Order',
  booking: 'Select Booking',
  field_quote: 'Select Field Quote',
  lead: 'Select Lead',
};

export default function LinkOrderTargetModal({
  isOpen,
  onClose,
  onSelect,
  allowUnlink = false,
}: LinkOrderTargetModalProps) {
  const { selectedBusiness } = useBusiness();
  const [view, setView] = useState<LinkView>('options');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<RowItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setView('options');
      setSearch('');
      setRows([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || view === 'options' || !selectedBusiness?.id || !supabase) return;
    const businessId = selectedBusiness.id;
    let stale = false;

    // Fetch customer names in a second query: embeds on bookings/leads have a
    // history of PGRST201 ambiguity issues, so we don't rely on them here.
    const fetchCustomerNames = async (customerIds: string[]): Promise<Map<string, string>> => {
      const ids = [...new Set(customerIds.filter(Boolean))];
      if (ids.length === 0) return new Map();
      const { data } = await supabase!
        .from('customers')
        .select('id, first_name, last_name')
        .in('id', ids);
      return new Map((data || []).map((c: any) => [c.id, customerName(c)]));
    };

    const load = async () => {
      setLoading(true);
      try {
        if (view === 'booking') {
          const { data, error } = await supabase!
            .from('bookings')
            .select('id, title, booking_number, start_date, customer_id, total_amount, booking_address')
            .eq('business_id', businessId)
            .order('start_date', { ascending: false, nullsFirst: false })
            .limit(50);
          if (error) throw error;
          const names = await fetchCustomerNames((data || []).map((b: any) => b.customer_id));
          if (stale) return;
          setRows((data || []).map((b: any) => ({
            key: b.id,
            label: b.booking_number ? `Booking #${b.booking_number}` : (b.title || 'Booking'),
            sub: [names.get(b.customer_id), fmtDate(b.start_date), fmtPrice(b.total_amount)].filter(Boolean).join(' · '),
            extra: bookingAddress(b) || undefined,
            selection: {
              type: 'booking',
              id: b.id,
              label: b.booking_number ? `Booking #${b.booking_number}` : (b.title || 'Booking'),
            },
          })));
        } else if (view === 'field_quote') {
          const { data, error } = await supabase!
            .from('project_field_quotes')
            .select('id, start_date, field_quote_address, created_at, customer:customers(first_name, last_name)')
            .eq('business_id', businessId)
            .order('start_date', { ascending: false, nullsFirst: false })
            .order('created_at', { ascending: false })
            .limit(50);
          if (error) throw error;
          if (stale) return;
          setRows((data || []).map((q: any) => ({
            key: q.id,
            label: customerName(q.customer) || 'Field Quote',
            sub: [fmtDate(q.start_date || q.created_at), q.field_quote_address].filter(Boolean).join(' · '),
            selection: {
              type: 'field_quote',
              id: q.id,
              label: `Field Quote · ${customerName(q.customer) || fmtDate(q.start_date || q.created_at)}`,
            },
          })));
        } else {
          const { data, error } = await supabase!
            .from('pipeline_opportunities')
            .select('id, status, source, created_at, customer_id')
            .eq('business_id', businessId)
            .order('created_at', { ascending: false })
            .limit(50);
          if (error) throw error;
          const names = await fetchCustomerNames((data || []).map((o: any) => o.customer_id));
          if (stale) return;
          setRows((data || []).map((o: any) => ({
            key: String(o.id),
            label: names.get(o.customer_id) || 'Lead',
            sub: [o.status, o.source, fmtDate(o.created_at)].filter(Boolean).join(' · '),
            selection: {
              type: 'lead',
              id: o.id,
              label: `Lead · ${names.get(o.customer_id) || `#${o.id}`}`,
            },
          })));
        }
      } catch (err) {
        console.error('Error loading link targets:', err);
        if (!stale) setRows([]);
      } finally {
        if (!stale) setLoading(false);
      }
    };

    load();
    return () => { stale = true; };
  }, [isOpen, view, selectedBusiness?.id]);

  if (!isOpen) return null;

  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter((r) => `${r.label} ${r.sub} ${r.extra ?? ''}`.toLowerCase().includes(q))
    : rows;

  const handleClose = () => {
    setView('options');
    setSearch('');
    onClose();
  };

  const handleBack = () => {
    setView('options');
    setSearch('');
  };

  const options: { view: LinkView; title: string; sub: string; icon: typeof Calendar; iconBg: string; iconColor: string }[] = [
    { view: 'booking', title: 'Bookings', sub: 'Link to a booking', icon: Calendar, iconBg: 'bg-blue-100', iconColor: 'text-blue-600' },
    { view: 'field_quote', title: 'Field Quotes', sub: 'Link to a field quote', icon: Ruler, iconBg: 'bg-teal-100', iconColor: 'text-teal-600' },
    { view: 'lead', title: 'Leads', sub: 'Link to an opportunity (lead)', icon: Users2, iconBg: 'bg-green-100', iconColor: 'text-green-600' },
  ];

  const searchPlaceholder =
    view === 'booking' ? 'Search bookings...' : view === 'field_quote' ? 'Search field quotes...' : 'Search leads...';
  const emptyLabel =
    view === 'booking' ? 'bookings' : view === 'field_quote' ? 'field quotes' : 'leads';

  return createPortal(
    <div
      className="fixed inset-0 transition-opacity duration-200 bg-black/50"
      style={{ zIndex: 100300 }}
      onClick={handleClose}
    >
      <div
        className="fixed inset-y-0 right-0 w-full md:w-[600px] bg-white shadow-2xl transform transition-transform duration-200 translate-x-0"
        onClick={(e) => e.stopPropagation()}
        style={{
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
        }}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">{VIEW_TITLES[view]}</h3>
            <button
              onClick={handleClose}
              className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {view === 'options' ? (
              <div className="space-y-3">
                {options.map(({ view: optionView, title, sub, icon: Icon, iconBg, iconColor }) => (
                  <button
                    key={optionView}
                    type="button"
                    onClick={() => setView(optionView)}
                    className="w-full px-4 py-3 text-left bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100 transition-all duration-150"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`h-10 w-10 rounded-lg ${iconBg} flex items-center justify-center`}>
                        <Icon className={`h-5 w-5 ${iconColor}`} />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-900">{title}</div>
                        <div className="text-xs text-gray-500">{sub}</div>
                      </div>
                    </div>
                  </button>
                ))}

                {allowUnlink && (
                  <button
                    type="button"
                    onClick={() => { onSelect({ type: 'none' }); handleClose(); }}
                    className="w-full px-4 py-3 text-left bg-white border border-red-200 rounded-lg hover:bg-red-50 hover:border-red-300 active:bg-red-100 transition-all duration-150"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                        <Link2Off className="h-5 w-5 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-red-600">Remove Link</div>
                        <div className="text-xs text-gray-500">Unlink this order from its job</div>
                      </div>
                    </div>
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Back Button */}
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
                >
                  <X className="h-4 w-4 mr-1 rotate-45" />
                  Back
                </button>

                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={searchPlaceholder}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>

                {/* Results List */}
                {loading ? (
                  <div className="text-center py-8 text-gray-500 text-sm">Loading {emptyLabel}...</div>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    {search ? `No ${emptyLabel} found` : `No ${emptyLabel} available`}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filtered.map((r) => (
                      <button
                        key={r.key}
                        type="button"
                        onClick={() => { onSelect(r.selection); handleClose(); }}
                        className="w-full px-4 py-3 text-left bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-150"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900 truncate">{r.label}</div>
                            {r.sub && <div className="text-xs text-gray-500 mt-0.5 truncate">{r.sub}</div>}
                            {r.extra && <div className="text-xs text-gray-400 mt-0.5 line-clamp-2">{r.extra}</div>}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
